
import { useState } from "react";
import { TikTokVideo } from "@/types/api";
import { toast } from "@/hooks/use-toast";
import JSZip from "jszip";
import { ProgressState } from "@/hooks/useBulkDownload";

export function useDownloadControls(
  downloadList: TikTokVideo[],
  progressState: ProgressState,
  setProgressState: (state: ProgressState | ((prev: ProgressState) => ProgressState)) => void,
  downloadControllers: React.MutableRefObject<{ [id: string]: AbortController }>
) {
  const [pausedVideos, setPausedVideos] = useState<string[]>([]);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [cancelAll, setCancelAll] = useState(false);

  const handlePauseOne = (id: string) => {
    setPausedVideos((pv) => [...pv, id]);
    setProgressState((state) => ({
      ...state,
      [id]: {
        ...state[id],
        status: "paused",
        retries: typeof state[id]?.retries === "number" ? state[id]!.retries : 0,
      },
    }));
    if (downloadControllers.current[id]) downloadControllers.current[id].abort();
  };

  const handleResumeOne = (id: string) => {
    setPausedVideos((pv) => pv.filter((vId) => vId !== id));
    setProgressState((state) => ({
      ...state,
      [id]: {
        ...state[id],
        status: "not_started",
        retries: typeof state[id]?.retries === "number" ? state[id]!.retries : 0,
      },
    }));
  };

  const handlePauseAll = () => {
    setPausedVideos(downloadList.map((v) => v.id));
    setProgressState((state) =>
      Object.fromEntries(
        Object.entries(state).map(([id, val]) =>
          downloadList.find(v => v.id === id) && val.status === "in_progress"
            ? [id, { ...val, status: "paused", retries: typeof val.retries === "number" ? val.retries : 0 }]
            : [id, val]
        )
      )
    );
  };

  const handleResumeAll = () => {
    setPausedVideos([]);
  };

  const handleCancelAll = () => {
    setCancelAll(true);
    setPausedVideos([]);
    setIsBulkDownloading(false);
    setProgressState((state) =>
      Object.fromEntries(
        Object.entries(state).map(([id, val]) =>
          downloadList.find(v => v.id === id) && (val.status === "in_progress" || val.status === "paused")
            ? [id, { ...val, status: "failed", error: "Cancelled", retries: typeof val.retries === "number" ? val.retries : 0 }]
            : [id, val]
        )
      )
    );
    Object.values(downloadControllers.current).forEach(c => c.abort());
  };

  const handleRetryOne = (id: string) => {
    setProgressState((state) => ({
      ...state,
      [id]: {
        ...state[id],
        status: "not_started",
        retries: 0,
      }
    }));
    setPausedVideos((pv) => pv.filter((vId) => vId !== id));
  };

  const handleDownloadZip = async () => {
    const zip = new JSZip();
    let hasCompletedVideos = false;

    downloadList.forEach((video) => {
      const ps = progressState[video.id];
      if (ps?.status === "complete" && ps?.blob) {
        hasCompletedVideos = true;
        const username = typeof video.author === 'string' 
          ? video.author.replace(/[^a-zA-Z0-9-_]/g, "_") 
          : (video.author?.uniqueId || "unknown").replace(/[^a-zA-Z0-9-_]/g, "_");
        zip.file(`${username}.mp4`, ps.blob);
      }
    });

    if (!hasCompletedVideos) {
      toast({
        title: "No videos ready to download",
        variant: "destructive",
      });
      return;
    }

    const content = await zip.generateAsync({ type: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(content);
    a.download = "tiktok-collection.zip";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 100);

    toast({
      title: "Download ready!",
      description: "Only videos marked 'Complete' have been included.",
      variant: "default",
    });
  };

  return {
    pausedVideos,
    setPausedVideos,
    isBulkDownloading,
    setIsBulkDownloading,
    cancelAll,
    setCancelAll,
    handlePauseOne,
    handleResumeOne,
    handlePauseAll,
    handleResumeAll,
    handleCancelAll,
    handleRetryOne,
    handleDownloadZip
  };
}
